import React, { useEffect, useState } from 'react'
import { useQuizStore } from '../store/quizStore'
import { patientQuizData, caregiverQuizData } from '../data/quizData'
import { QuestionPage } from './QuestionPage'
import { InformationalPage } from './InformationalPage'
import { PersonalizedUpsellSlide } from './PersonalizedUpsellSlide'
import { ProgressBar } from './ProgressBar'
import { Loader } from './Loader'
import { analyzeAnswers, generatePersonalizedSummary, generatePersonalizedHope } from '../utils/analyzeAnswers'
import { trackPageViewed, trackQuestionAnswered, trackQuizCompleted } from '../utils/tracking/tracking'

interface QuizContainerProps {
  quizType: 'patient' | 'caregiver'
  onComplete?: () => void
}

export const QuizContainer: React.FC<QuizContainerProps> = ({
  quizType,
  onComplete,
}) => {
  const session = useQuizStore((state) => state.session)
  const initSession = useQuizStore((state) => state.initSession)
  const movePage = useQuizStore((state) => state.movePage)
  const goBackPage = useQuizStore((state) => state.goBackPage)
  const completeQuiz = useQuizStore((state) => state.completeQuiz)
  const abandonQuiz = useQuizStore((state) => state.abandonQuiz)

  const [gender, setGender] = useState<'male' | 'female' | 'female_caregiver' | 'male_caregiver'>('female')
  const [currentPageKey, setCurrentPageKey] = useState<string>('home')
  const [pageHistory, setPageHistory] = useState<string[]>(['home'])
  const [sessionStartTime] = useState<number>(Date.now())

  const quizData = quizType === 'patient' ? patientQuizData : caregiverQuizData
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const totalPages = (quizData as any).metadata?.totalPages || 30


  useEffect(() => {
    if (!session) {
      initSession(quizType);
      // Track initial page view
      trackPageViewed(0, 'home');
    }
  }, [])

  // Track page view when page changes
  useEffect(() => {
    if (session && currentPage) {
      trackPageViewed(currentPage.pageNum || 0, currentPageKey);
    }
  }, [currentPageKey])

  // Track quiz abandonment when component unmounts or user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (session && session.status === 'in_progress') {
        abandonQuiz(session.currentPage);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session, abandonQuiz])

  // Handle URL-based navigation
  useEffect(() => {
    const path = window.location.pathname
    // Match /question/27 or /question/patient/27 or /question/caregiver/27
    const match = path.match(/\/question\/(?:patient|caregiver)?\/?\d+/)
      ? path.match(/(\d+)$/)
      : null

    if (match) {
      const pageNum = parseInt(match[1], 10)
      // Find the page key that matches the pageNum
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const quizDataObj = quizData as any

      for (const key in quizDataObj) {
        const page = quizDataObj[key]
        if (page && page.pageNum === pageNum) {
          setCurrentPageKey(key)
          setPageHistory([key])
          break
        }
      }
    }
  }, [quizData])

  if (!session) {
    return <div>Loading quiz...</div>
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentPage = (quizData as any)[currentPageKey]
  if (!currentPage) {
    return <div>Page not found: {currentPageKey}</div>
  }

  const handlePageNext = (answer?: string | string[]) => {
    // Scroll to top immediately for smooth transition
    window.scrollTo(0, 0)

    const nextPageKey = currentPage.nextPage || 'home'

    // Track question answer if provided
    if (answer && currentPage.question) {
      trackQuestionAnswered(currentPage.pageNum || 0, currentPage.question, answer);
    }

    // Handle gender selection - use actual answer value
    if (currentPageKey === 'home' || currentPageKey === 'page0_gender' || currentPageKey === 'page1_gender') {
      if (typeof answer === 'string') {
        // Map non-binary options to a gender for content display
        let genderValue: 'male' | 'female' | 'female_caregiver' | 'male_caregiver' = answer as any
        if (answer === 'other') genderValue = 'female'
        if (answer === 'other_caregiver') genderValue = 'female_caregiver'
        setGender(genderValue)
      }
    }

    if (currentPage.pageNum === totalPages - 1) {
      completeQuiz();
      // Track quiz completion
      const totalTimeMs = Date.now() - sessionStartTime;
      trackQuizCompleted(quizType, totalTimeMs, totalPages);
      onComplete?.();
    } else {
      movePage(currentPage.pageNum + 1)
      setCurrentPageKey(nextPageKey)
      setPageHistory([...pageHistory, nextPageKey])
    }
  }

  const handlePageBack = () => {
    // Scroll to top immediately for smooth transition
    window.scrollTo(0, 0)

    if (pageHistory.length > 1) {
      const previousPageKey = pageHistory[pageHistory.length - 2]
      const newHistory = pageHistory.slice(0, -1)
      setPageHistory(newHistory)
      setCurrentPageKey(previousPageKey)

      // Get the pageNum of the previous page and update session
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const previousPage = (quizData as any)[previousPageKey]
      if (previousPage) {
        goBackPage(previousPage.pageNum || 0)
      }
    }
  }


  return (
    <div className="quiz-wrapper">
      <div className="quiz-header-bar">
        <ProgressBar
          current={(currentPage.pageNum || 0) + 1}
          total={totalPages}
          onBack={handlePageBack}
          canGoBack={(currentPage.pageNum || 0) > 0}
        />
      </div>

      {currentPage.loading || currentPage.isLoadingPage ? (
        <Loader
          heading={currentPage.heading}
          steps={currentPage.steps || currentPage.loadingSteps || []}
          nextPage={currentPage.nextPage}
          onComplete={() => handlePageNext()}
        />
      ) : currentPage.type === 'question' ? (
        <QuestionPage
          pageNum={currentPage.pageNum}
          question={currentPage.question}
          subtext={currentPage.subtext}
          options={currentPage.options}
          singleSelect={currentPage.singleSelect}
          onNext={handlePageNext}
        />
      ) : currentPage.type === 'upsell' ? (
        <PersonalizedUpsellSlide
          userProfile={analyzeAnswers(session)}
          onComplete={() => {
            completeQuiz();
            const totalTimeMs = Date.now() - sessionStartTime;
            trackQuizCompleted(quizType, totalTimeMs, totalPages);
          }}
        />
      ) : (
        (() => {
          // Generate personalized content if needed
          let messageToShow = currentPage.message || currentPage.content

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((currentPage as any).isPersonalized && quizType === 'patient') {
            const userProfile = analyzeAnswers(session)
            const personalizedStart = generatePersonalizedSummary(userProfile)
            const personalizedHope = generatePersonalizedHope(userProfile)
            messageToShow = `${personalizedStart}\n\n${currentPage.baseContent || ''}\n\n${personalizedHope}`
          }

          return (
            <InformationalPage
              headline={currentPage.headline}
              heading={currentPage.heading}
              subheading={currentPage.subheading}
              message={messageToShow}
              image={currentPage.image}
              contentByGender={currentPage.contentByGender}
              benefits={currentPage.benefits}
              trustBadges={currentPage.trustBadges}
              testimonial={currentPage.testimonial}
              ctaText={currentPage.ctaText}
              onNext={handlePageNext}
              gender={gender}
            />
          )
        })()
      )}
    </div>
  )
}
